import { useRef } from "react";
import { motion, AnimatePresence, type PanInfo } from "framer-motion";
import type { Location } from "../data/locations.js";

interface LocationSheetProps {
  location: Location | null;
  onClose: () => void;
}

export function LocationSheet({ location, onClose }: LocationSheetProps) {
  const carouselRef = useRef<HTMLDivElement>(null);

  return (
    <AnimatePresence>
      {location && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{ background: "rgba(0,0,0,0.15)" }}
          />

          {/* Sheet */}
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-sheet"
            style={{
              maxHeight: "75vh",
              boxShadow: "0 -8px 40px rgba(26,25,23,0.15), 0 -2px 12px rgba(26,25,23,0.08)",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 350, damping: 35 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={(_: unknown, info: PanInfo) => {
              if (info.offset.y > 100 || info.velocity.y > 500) {
                onClose();
              }
            }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="sheet-handle" />
            </div>

            <div className="px-5 pb-6 overflow-y-auto" style={{ maxHeight: "calc(75vh - 40px)" }}>
              {/* Header with number badge */}
              <div className="flex items-center gap-3 mb-1">
                <span
                  className="flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold flex-shrink-0"
                  style={{ backgroundColor: "#B76E6E" }}
                >
                  {location.number}
                </span>
                <div className="flex items-baseline gap-2 min-w-0">
                  <h2 className="text-lg font-semibold text-ink-0 font-display truncate">
                    {location.name}
                  </h2>
                  <span className="text-sm text-ink-2 flex-shrink-0">{location.nameJa}</span>
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-ink-1 leading-relaxed mb-4 ml-10">
                {location.description}
              </p>

              {/* Photo carousel */}
              <div
                ref={carouselRef}
                className="flex gap-3 overflow-x-auto carousel-scroll -mx-5 px-5 pb-1 snap-x snap-mandatory"
              >
                {location.photos.map((photo, i) => (
                  <div
                    key={i}
                    className="flex-shrink-0 snap-center rounded-2xl overflow-hidden"
                    style={{ width: "72vw", maxWidth: "320px" }}
                  >
                    <img
                      src={photo.url}
                      alt={photo.alt}
                      className="w-full aspect-[3/4] object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>

              {/* Tip */}
              {location.tip && (
                <div className="mt-4 flex items-start gap-2 px-3 py-2.5 bg-surface-1 rounded-xl">
                  <span className="text-sm leading-none mt-0.5">📍</span>
                  <p className="text-xs text-ink-1 leading-relaxed">{location.tip}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-5 flex gap-3">
                {/* Google Maps link */}
                <a
                  href={location.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl border-2 text-sm font-medium transition-all active:scale-[0.98]"
                  style={{ borderColor: "#B76E6E", color: "#B76E6E" }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  Open in Maps
                </a>

                {/* Book CTA */}
                <a
                  href="https://kra-booking.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-center py-3 rounded-2xl text-white text-sm font-medium transition-all active:scale-[0.98]"
                  style={{ backgroundColor: "#B76E6E" }}
                >
                  Book a shoot →
                </a>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
